import { useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { handleAuthError } from '../lib/errors'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (error) {
        console.error('Error getting session:', error)
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)

        // Create user profile on sign up
        if (event === 'SIGNED_IN' && session?.user) {
          await createProfileIfNeeded(session.user)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const createProfileIfNeeded = async (user: User) => {
    try {
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single()

      // Create profile if it doesn't exist
      if (!existingProfile) {
        const { error } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            display_name: user.email?.split('@')[0] || 'Ultra Runner'
          })

        if (error) {
          console.error('Error creating profile:', error)
        }
      }
    } catch (error) {
      console.error('Error checking/creating profile:', error)
    }
  }

  const signInWithEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      })
      
      if (error) throw handleAuthError(error)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }


  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw handleAuthError(error)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  return {
    user,
    session,
    loading,
    signInWithEmail,
    signOut
  }
}
